import React from 'react';
import { Search, Database, MessageSquare, Clock } from 'lucide-react';
import { problemsData } from '../../data/mock';

const iconMap = {
  Search: Search,
  Database: Database,
  MessageSquare: MessageSquare,
  Clock: Clock
};

// Custom Mountain Icon with solid fill
const MountainIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 22H22L12 2ZM12 6L18.5 19H5.5L12 6Z" />
    <path d="M12 6L18.5 19H5.5L12 6Z" fill="currentColor" opacity="0.3" />
    <path d="M7 14L10 9L13 14H7Z" />
  </svg>
);

const ProblemsSection = () => {
  return (
    <section id="challenge" className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="heading-1 mb-4 flex items-center justify-center gap-3 flex-wrap">
            <svg className="w-10 h-10 sm:w-12 sm:h-12 text-[var(--brand-teal-600)]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L1 21h22L12 2zm0 3.5L19.5 19h-15L12 5.5z"/>
              <path d="M12 5.5L4.5 19h15L12 5.5z" fillOpacity="0.3"/>
              <path d="M8 15l3-5 3 5H8z"/>
            </svg>
            <span>This is the Challenge <span className="gradient-text">We Solve</span></span>
          </h2>
          <p className="body-large max-w-3xl mx-auto">
            Whether you&apos;re hunting candidates or clients, fragmented tools and siloed workflows 
            stand between you and the people who matter most.
          </p>
        </div>

        {/* Problem Cards Grid - Compact Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          {problemsData.map((problem) => {
            const IconComponent = iconMap[problem.icon];
            return (
              <div
                key={problem.id}
                className="group relative bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 sm:p-5 border border-gray-100 transition-all duration-300 hover:shadow-lg hover:border-[var(--brand-teal-200)] hover:-translate-y-1"
              >
                {/* Header Row - Icon and Title side by side */}
                <div className="flex items-center gap-3 sm:gap-4 mb-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-[var(--brand-teal-100)] to-[var(--brand-teal-50)] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--brand-teal-600)]" />
                  </div>
                  <h3 className="heading-3 text-base sm:text-lg">{problem.title}</h3>
                </div>

                {/* Description */}
                <p className="body-medium text-sm mb-3 pl-[52px] sm:pl-16">{problem.description}</p>

                {/* Impact Tag - Red/Warning style */}
                <div className="pl-[52px] sm:pl-16">
                  <div className="inline-flex items-center gap-2 bg-red-50 rounded-full px-3 py-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                    <span className="text-xs font-medium text-red-600">{problem.impact}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Message */}
        <div className="mt-8 text-center">
          <p className="body-large text-[var(--text-muted)] italic">
            &ldquo;You cannot reach the right people if their contactability is fragmented.&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProblemsSection;
